import { TableColumnSortOption } from './types';

export interface Sort<T> {
  order(a: T, b: T): number;
}

export interface SortOrder {
  before(): number;
  after(): number;
  equal(): number;
}

export class AscendingOrder implements SortOrder {
  before(): number {
    return -1;
  }
  after(): number {
    return 1;
  }
  equal(): number {
    return 0;
  }
}

export class DescendingOrder implements SortOrder {
  before(): number {
    return 1;
  }
  after(): number {
    return -1;
  }
  equal(): number {
    return 0;
  }
}

export class StringSort implements Sort<string> {
  constructor(private readonly _order: SortOrder) {}

  public order(a: string, b: string): number {
    const valueA = a.toUpperCase();
    const valueB = b.toUpperCase();

    if (valueA < valueB) {
      return this._order.before();
    }
    if (valueA > valueB) {
      return this._order.after();
    }

    return this._order.equal();
  }
}

export class NumberSort implements Sort<number> {
  constructor(private readonly _order: SortOrder) {}

  public order(a: number, b: number): number {
    if (a < b) {
      return this._order.before();
    }
    if (a > b) {
      return this._order.after();
    }

    return this._order.equal();
  }
}

export class DateSort implements Sort<Date> {
  constructor(private readonly _order: SortOrder) {}

  public order(a: Date, b: Date): number {
    const time = b.getTime() - a.getTime();

    if (time > 0) {
      return this._order.before();
    }

    if (time < 0) {
      return this._order.after();
    }

    return this._order.equal();
  }
}

export class TableSort implements Sort<unknown> {
  private readonly _string: Sort<string>;
  private readonly _number: Sort<number>;
  private readonly _date: Sort<Date>;

  constructor(
    private readonly _order: SortOrder,
    private readonly enable: boolean = true
  ) {
    this._string = new StringSort(_order);
    this._number = new NumberSort(_order);
    this._date = new DateSort(_order);
  }

  public static from(sort: TableColumnSortOption): TableSort {
    const order =
      sort === 'ascending' ? new AscendingOrder() : new DescendingOrder();
    return new TableSort(order, sort !== 'none');
  }

  public order(a: unknown, b: unknown): number {
    if (!this.enable) {
      return this._order.equal();
    }

    if (typeof a === 'string' && typeof b === 'string') {
      return this._string.order(a, b);
    }

    if (typeof a === 'number' && typeof b === 'number') {
      return this._number.order(a, b);
    }

    if (a instanceof Date && b instanceof Date) {
      return this._date.order(a, b);
    }

    // Don't know which type of sort to apply
    return this._order.equal();
  }
}
